import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Eye, MessageCircle, Minus, Pause, Play, Plus, RotateCw, Save, Square, X } from "lucide-react";
import api from "@/lib/api";
import { SPIN_DURATION_MS, SPIN_STAGES, createSpinPlan, crossedPins, pinAngles, safeWinningAngle, spinStage, weightedSpinProgress } from "./wheelMechanics";

const colors = ["#22d3ee", "#7c3aed", "#f4d47c", "#2563eb", "#c084fc", "#0891b2", "#f59e0b", "#4f46e5"];
const defaults = [
  { label: "Pregunta del Oráculo", kind: "question", value: 0, delivery_mode: "automatic", payload: "¿Qué aprendizaje te deja este encuentro?" },
  { label: "Sello virtual", kind: "virtual_gift", value: 0, delivery_mode: "automatic", payload: "Sello de gratitud del Templo" },
  { label: "Mensaje ancestral", kind: "message", value: 0, delivery_mode: "automatic", payload: "Tu presencia transforma el espacio" },
  { label: "Experiencia KD9", kind: "experience", value: 0, delivery_mode: "automatic", payload: "El anfitrión guía un momento especial" },
];
const initial = { enabled: false, status: "draft", title: "Ruleta del Templo", spin_cost_energy: 0, duration_ms: 4000, options: defaults };

const allowedKinds = new Set(["question", "virtual_gift", "message", "experience", "discount"]);
const allowedDeliveryModes = new Set(["live", "private", "automatic"]);

function normalizeWheelConfig(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const rows = Array.isArray(source.options) && source.options.length >= 2 ? source.options : defaults;
  const options = rows.slice(0, 24).map((item, index) => {
    const row = item && typeof item === "object" ? item : {};
    const label = String(row.label || "").trim() || `Segmento ${index + 1}`;
    const numericValue = Number(row.value);
    return {
      label: label.slice(0, 80),
      kind: allowedKinds.has(row.kind) ? row.kind : "experience",
      value: Number.isFinite(numericValue) ? Math.max(0, Math.min(10000, numericValue)) : 0,
      payload: row.payload == null ? "" : String(row.payload).slice(0, 240),
      delivery_mode: allowedDeliveryModes.has(row.delivery_mode) ? row.delivery_mode : "automatic",
    };
  });
  const title = String(source.title || initial.title).trim() || initial.title;
  const duration = Number(source.duration_ms);
  const cost = Number(source.spin_cost_energy);
  return {
    ...initial,
    ...source,
    title: title.slice(0, 80),
    spin_cost_energy: Number.isFinite(cost) ? Math.max(0, cost) : 0,
    duration_ms: Number.isFinite(duration) ? Math.max(3000, Math.min(5000, duration)) : 4000,
    options,
  };
}

let tickAudioContext = null;

function prepareMechanicalAudio() {
  try {
    const Ctx = globalThis.AudioContext || globalThis.webkitAudioContext;
    if (!Ctx) return null;
    tickAudioContext = tickAudioContext || new Ctx();
    if (tickAudioContext.state === "suspended") tickAudioContext.resume().catch(() => {});
    return tickAudioContext;
  } catch (_) {
    return null;
  }
}

function playMechanicalTicks(crossings, frameSeconds, finalBoundary) {
  const ctx = prepareMechanicalAudio();
  if (!ctx) return;
  const frameWindow = Math.min(0.025, Math.max(0, frameSeconds));
  crossings.forEach(crossing => {
    const isFinal = crossing.boundaryIndex === finalBoundary;
    const startsAt = ctx.currentTime + crossing.frameFraction * frameWindow;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = isFinal ? "square" : "triangle";
    oscillator.frequency.setValueAtTime(isFinal ? 210 : 360, startsAt);
    oscillator.frequency.exponentialRampToValueAtTime(isFinal ? 75 : 120, startsAt + 0.032);
    gain.gain.setValueAtTime(0.0001, startsAt);
    gain.gain.exponentialRampToValueAtTime(isFinal ? 0.075 : 0.04, startsAt + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + (isFinal ? 0.052 : 0.035));
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(startsAt);
    oscillator.stop(startsAt + (isFinal ? 0.055 : 0.038));
  });
}

function flexMechanicalPointer(pointer, isFinal) {
  if (!pointer) return;
  pointer.classList.remove("is-ticking", "is-final-tick");
  void pointer.offsetWidth;
  pointer.classList.add(isFinal ? "is-final-tick" : "is-ticking");
}

function RadialWheel({ wheel, angle = 0, result, rotorRef, pointerRef }) {
  const safeWheel = normalizeWheelConfig(wheel);
  const options = safeWheel.options;
  const count = Math.max(1, options.length);
  const localRotorRef = useRef(null);
  const activeRotorRef = rotorRef || localRotorRef;
  const gradient = options.map((_, i) => {
    const a = (i * 360) / count, b = ((i + 1) * 360) / count;
    return `${colors[i % colors.length]} ${a}deg ${b}deg`;
  }).join(",");

  useEffect(() => {
    if (activeRotorRef.current) activeRotorRef.current.style.transform = `rotate(${angle}deg)`;
  }, [activeRotorRef, angle]);

  return <div className="relative mx-auto aspect-square w-[min(76vw,300px)]" aria-label="Ruleta radial del Templo">
    <div ref={pointerRef} className="kd9-wheel-pointer" aria-hidden="true"><span /></div>
    <div ref={activeRotorRef} className="kd9-wheel-rotor">
      <div className="kd9-temple-wheel absolute inset-0 rounded-full border-[5px] border-cyan-100/70 shadow-[0_0_42px_rgba(34,211,238,.42)]"
        style={{ background: `conic-gradient(${gradient})` }}>
        {options.map((option, index) => {
          const deg = (index + .5) * 360 / count;
          return <span key={index} className="absolute left-1/2 top-1/2 w-[42%] origin-left truncate text-[10px] font-black text-white drop-shadow-md"
            style={{ transform: `rotate(${deg - 90}deg) translateX(18%)` }}>{option.label}</span>;
        })}
        <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-cyan-100 bg-[#080b18] text-lg">⚡</span>
      </div>
      {pinAngles(count).map((pinAngle, index) =>
        <span key={index} className="kd9-wheel-pin-track" style={{ transform: `rotate(${pinAngle}deg)` }} aria-hidden="true">
          <i className="kd9-wheel-pin" />
        </span>
      )}
    </div>
    {result && <div className="absolute inset-x-3 bottom-3 z-20 rounded-xl border border-cyan-200/50 bg-[#050816]/95 p-3 text-center">
      <b className="text-cyan-100">{result.label}</b>{result.payload && <p className="text-xs text-white/75">{result.payload}</p>}
    </div>}
  </div>;
}

export function TempleWheelEvent({ wheel, event, onComplete }) {
  const [angle,setAngle]=useState(0);
  const rotorRef=useRef(null), pointerRef=useRef(null), animationRef=useRef(0), angleRef=useRef(0);
  const onCompleteRef=useRef(onComplete);
  const eventRef=useRef(event);
  onCompleteRef.current=onComplete;
  eventRef.current=event;
  const eventWheel=useMemo(()=>{
    const count=Math.max(2,Number(event?.segment_count)||wheel?.options?.length||2);
    const snapshot=Array.isArray(event?.wheel_labels)&&event.wheel_labels.length===count
      ? event.wheel_labels.map(label=>({label}))
      : wheel?.options?.length===count
        ? wheel.options
        : Array.from({length:count},(_,index)=>({label:`Segmento ${index+1}`}));
    return {...wheel,options:snapshot};
  },[event,wheel]);

  useEffect(()=>{
    const spinEvent=eventRef.current;
    if(!spinEvent?.id)return undefined;
    prepareMechanicalAudio();
    cancelAnimationFrame(animationRef.current);
    const count=eventWheel.options.length;
    const target=safeWinningAngle(spinEvent,count);
    const plan=createSpinPlan(angleRef.current,target,count);
    const step=360/count;
    const finalBoundary=Math.floor((plan.endAngle+1e-7)/step);
    const startedAt=performance.now();
    let previousAngle=plan.startAngle;
    let previousFrame=startedAt;
    rotorRef.current?.classList.add("is-spinning");

    const frame=(now)=>{
      const elapsed=Math.min(SPIN_DURATION_MS,now-startedAt);
      const progress=weightedSpinProgress(elapsed,SPIN_DURATION_MS);
      const nextAngle=plan.startAngle+(plan.endAngle-plan.startAngle)*progress;
      if(rotorRef.current){
        rotorRef.current.style.transform=`rotate(${nextAngle}deg)`;
        rotorRef.current.dataset.spinStage=SPIN_STAGES[spinStage(elapsed)];
      }
      const crossings=crossedPins(previousAngle,nextAngle,count);
      if(crossings.length){
        const isFinal=crossings.some(crossing=>crossing.boundaryIndex===finalBoundary);
        playMechanicalTicks(crossings,(now-previousFrame)/1000,finalBoundary);
        flexMechanicalPointer(pointerRef.current,isFinal);
      }
      previousAngle=nextAngle;
      previousFrame=now;
      if(elapsed<SPIN_DURATION_MS){
        animationRef.current=requestAnimationFrame(frame);
        return;
      }
      angleRef.current=plan.endAngle;
      if(rotorRef.current){
        rotorRef.current.style.transform=`rotate(${plan.endAngle}deg)`;
        rotorRef.current.classList.remove("is-spinning");
        delete rotorRef.current.dataset.spinStage;
      }
      setAngle(plan.endAngle);
      navigator.vibrate?.([70,35,90]);
      onCompleteRef.current?.(spinEvent);
    };
    animationRef.current=requestAnimationFrame(frame);
    return()=>cancelAnimationFrame(animationRef.current);
  },[event?.id,eventWheel.options.length]);

  return <RadialWheel wheel={eventWheel} angle={angle} rotorRef={rotorRef} pointerRef={pointerRef}/>;
}

function Stepper({ label, value, min, max, onDecrease, onIncrease }) {
  return <div>
    <span className="text-xs">{label}</span>
    <div className="mt-1 grid min-h-12 grid-cols-[3rem_1fr_3rem] overflow-hidden rounded-xl border border-cyan-300/25 bg-[#080b18]" role="group" aria-label={label}>
      <button type="button" className="grid min-h-12 place-items-center border-r border-white/10 text-cyan-100 transition hover:bg-cyan-400/10 active:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-35" onClick={onDecrease} disabled={value <= min} aria-label={`Disminuir ${label.toLowerCase()}`}>
        <Minus size={20} aria-hidden="true"/>
      </button>
      <output className="grid min-w-0 place-items-center px-2 text-base font-bold tabular-nums text-white" aria-live="polite">{value}</output>
      <button type="button" className="grid min-h-12 place-items-center border-l border-white/10 text-cyan-100 transition hover:bg-cyan-400/10 active:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-35" onClick={onIncrease} disabled={value >= max} aria-label={`Aumentar ${label.toLowerCase()}`}>
        <Plus size={20} aria-hidden="true"/>
      </button>
    </div>
  </div>;
}

function Editor({ value, onChange, onSave, onClose, saving, live }) {
  const setCount = (count) => {
    const safe = Math.max(2, Math.min(12, Number(count) || 2));
    if (safe < value.options.length) {
      const removed = value.options.slice(safe);
      const hasConfiguredResults = removed.some((option, offset) => {
        const position = safe + offset + 1;
        return option.payload?.trim() || Number(option.value) > 0 || option.kind !== "experience" || (option.label?.trim() && option.label.trim() !== `Segmento ${position}`);
      });
      if (hasConfiguredResults && !globalThis.confirm("Los segmentos que vas a eliminar tienen premios o resultados configurados. ¿Deseas continuar?")) return;
    }
    const options = Array.from({ length: safe }, (_, i) => value.options[i] || { label: `Segmento ${i + 1}`, kind: "experience", value: 0, delivery_mode: "automatic", payload: "" });
    onChange({ ...value, options });
  };
  return <div className="space-y-4" data-testid="wheel-builder">
    <div className="flex items-center justify-between"><div><b className="text-cyan-100">Preparar acontecimiento</b><p className="text-xs text-white/55">Configura → define → previsualiza → guarda</p></div>{onClose && <button onClick={onClose}><X /></button>}</div>
    <label className="block text-xs">1. Nombre<input className="kd9-input mt-1 w-full p-3" maxLength="80" value={value.title} onChange={e=>onChange({...value,title:e.target.value})}/></label>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="rounded-xl border border-cyan-300/20 bg-cyan-400/[.04] p-3">
        <span className="text-xs text-white/55">2. Costo por giro</span>
        <div className="mt-1 font-cinzel text-lg text-cyan-100">⚡ {Number(value.spin_cost_energy || 0).toLocaleString("es-CO")} KD</div>
        <p className="mt-1 text-[10px] text-white/45">Definido exclusivamente por Dirección en Centro de Costos KD9.</p>
      </div>
      <Stepper label="3. Segmentos" value={value.options.length} min={2} max={12}
        onDecrease={()=>setCount(value.options.length-1)}
        onIncrease={()=>setCount(value.options.length+1)}/>
    </div>
    <div className="space-y-2">{value.options.map((option,index)=><div key={index} className="rounded-xl border border-white/10 p-2">
      <div className="grid grid-cols-[1fr_8rem] gap-2"><input className="kd9-input p-2" maxLength="80" value={option.label} onChange={e=>{const options=[...value.options];options[index]={...option,label:e.target.value};onChange({...value,options});}}/>
      <select className="kd9-input p-2" value={option.kind} onChange={e=>{const options=[...value.options];options[index]={...option,kind:e.target.value,value:0};onChange({...value,options});}}>
        <option value="experience">Experiencia</option><option value="question">Pregunta</option><option value="virtual_gift">Regalo virtual</option><option value="message">Mensaje</option><option value="discount">Descuento</option>
      </select></div>
      <input className="kd9-input mt-2 w-full p-2" maxLength="240" placeholder="Premio o resultado que verá el participante" value={option.payload||""} onChange={e=>{const options=[...value.options];options[index]={...option,payload:e.target.value};onChange({...value,options});}}/>
      <label className="mt-2 block text-[11px] text-white/65">Entrega del premio
        <select className="kd9-input mt-1 w-full p-2" value={option.delivery_mode||"automatic"} onChange={e=>{const options=[...value.options];options[index]={...option,delivery_mode:e.target.value};onChange({...value,options});}}>
          <option value="live">En vivo · el anfitrión marca entregado</option>
          <option value="private">En privado · mensaje temporal al ganador</option>
          <option value="automatic">Automática · queda entregado al girar</option>
        </select>
      </label>
    </div>)}</div>
    <div><p className="mb-2 flex items-center gap-2 text-xs text-violet-100"><Eye size={14}/>Vista previa exacta del participante</p><RadialWheel wheel={value}/></div>
    <p className="text-[10px] text-white/45">La ruleta no crea Energía KD9. Los resultados económicos permitidos son descuentos o beneficios definidos.</p>
    <button disabled={saving || !Array.isArray(value?.options) || value.options.some(x=>!String(x?.label || "").trim())} onClick={onSave} className="kd9-btn-primary min-h-12 w-full px-4"><Save size={16}/>{saving?"Guardando…":live?"Guardar cambios":"Guardar preparación"}</button>
  </div>;
}

export function TempleWheelSetup() {
  const [wheel,setWheel]=useState(initial), [saving,setSaving]=useState(false), [notice,setNotice]=useState("");
  useEffect(()=>{api.get("/api/live/games/wheel-template").then(({data})=>setWheel(normalizeWheelConfig(data))).catch(()=>setWheel(normalizeWheelConfig(initial)));},[]);
  const save=async()=>{setSaving(true);setNotice("");try{const {data}=await api.put("/api/live/games/wheel-template",{...normalizeWheelConfig(wheel),status:"draft",enabled:false});setWheel(normalizeWheelConfig(data));setNotice("Ruleta preparada. Se copiará de forma aislada al próximo live.");}catch(e){setNotice(e?.response?.data?.detail?.message||"No pudimos guardar la ruleta. Revisa los segmentos.");}finally{setSaving(false);}};
  return <section className="mt-4 rounded-2xl border border-violet-400/25 bg-[#0b0714]/70 p-4"><Editor value={wheel} onChange={setWheel} onSave={save} saving={saving}/>{notice&&<p className="mt-3 text-sm text-cyan-100">{notice}</p>}</section>;
}

export default function TempleWheel({ sessionId, isHost, wallet, onWallet, onError, onConfirmedSpin, externalPresentation = false }) {
  const [wheel,setWheel]=useState(initial), [draft,setDraft]=useState(initial), [editing,setEditing]=useState(false);
  const [saving,setSaving]=useState(false), [spinning,setSpinning]=useState(false), [result,setResult]=useState(null);
  const [angle,setAngle]=useState(0), [history,setHistory]=useState([]);
  const [privateMessages,setPrivateMessages]=useState([]), [privateDrafts,setPrivateDrafts]=useState({});
  const [delivering,setDelivering]=useState(new Set());
  const keyRef=useRef(null), spinBusyRef=useRef(false), seen=useRef(new Set()), privateSeen=useRef(new Set());
  const rotorRef=useRef(null), pointerRef=useRef(null), animationRef=useRef(0), angleRef=useRef(0), wheelCountRef=useRef(2);
  const load=useCallback(async()=>{try{const [{data:w},{data:h},{data:m}]=await Promise.all([api.get(`/api/live/${sessionId}/games/wheel`),api.get(`/api/live/${sessionId}/games/events`,{params:{limit:12}}),api.get(`/api/live/${sessionId}/games/wheel/private-messages`)]);const safeWheel=normalizeWheelConfig(w);setWheel(safeWheel);setDraft(safeWheel);setHistory(h);setPrivateMessages(m);}catch(_){onError("No pudimos actualizar la ruleta. Reintentaremos automáticamente.");}},[sessionId,onError]);
  useEffect(()=>{load();const id=setInterval(load,5000);return()=>clearInterval(id);},[load]);
  useEffect(()=>{wheelCountRef.current=Math.max(2,wheel.options.length);},[wheel.options.length]);
  useEffect(()=>{
    const unlock=()=>prepareMechanicalAudio();
    globalThis.addEventListener?.("pointerdown",unlock,{once:true,passive:true});
    return()=>{globalThis.removeEventListener?.("pointerdown",unlock);cancelAnimationFrame(animationRef.current);};
  },[]);
  const reveal=useCallback((data)=>{
    if(!data?.id||seen.current.has(data.id))return;
    seen.current.add(data.id);
    cancelAnimationFrame(animationRef.current);
    setResult(null);
    setSpinning(true);
    const count=Math.max(2,Number(data.segment_count)||wheelCountRef.current);
    const target=safeWinningAngle(data,count);
    const plan=createSpinPlan(angleRef.current,target,count);
    const step=360/count;
    const finalBoundary=Math.floor((plan.endAngle+1e-7)/step);
    const startedAt=performance.now();
    let previousAngle=plan.startAngle;
    let previousFrame=startedAt;
    rotorRef.current?.classList.add("is-spinning");

    const frame=(now)=>{
      const elapsed=Math.min(SPIN_DURATION_MS,now-startedAt);
      const progress=weightedSpinProgress(elapsed,SPIN_DURATION_MS);
      const nextAngle=plan.startAngle+(plan.endAngle-plan.startAngle)*progress;
      if(rotorRef.current){
        rotorRef.current.style.transform=`rotate(${nextAngle}deg)`;
        rotorRef.current.dataset.spinStage=SPIN_STAGES[spinStage(elapsed)];
      }
      const crossings=crossedPins(previousAngle,nextAngle,count);
      if(crossings.length){
        const isFinal=crossings.some(crossing=>crossing.boundaryIndex===finalBoundary);
        playMechanicalTicks(crossings,(now-previousFrame)/1000,finalBoundary);
        flexMechanicalPointer(pointerRef.current,isFinal);
      }
      previousAngle=nextAngle;
      previousFrame=now;
      if(elapsed<SPIN_DURATION_MS){
        animationRef.current=requestAnimationFrame(frame);
        return;
      }
      angleRef.current=plan.endAngle;
      if(rotorRef.current){
        rotorRef.current.style.transform=`rotate(${plan.endAngle}deg)`;
        rotorRef.current.classList.remove("is-spinning");
        delete rotorRef.current.dataset.spinStage;
      }
      setAngle(plan.endAngle);
      setResult(data);
      setSpinning(false);
      setHistory(h=>[data,...h.filter(x=>x.id!==data.id)].slice(0,12));
      navigator.vibrate?.([70,35,90]);
    };
    animationRef.current=requestAnimationFrame(frame);
  },[]);
  useEffect(()=>{const es=new EventSource(`/api/live/${sessionId}/games/events/stream`,{withCredentials:true});es.addEventListener("wheel_result",e=>{try{const data=JSON.parse(e.data);if(!externalPresentation){if(onConfirmedSpin)onConfirmedSpin(data);else reveal(data);}}catch(_){}});es.addEventListener("private_prize",e=>{try{const message=JSON.parse(e.data);if(privateSeen.current.has(message.id))return;privateSeen.current.add(message.id);setPrivateMessages(rows=>[...rows.filter(x=>x.id!==message.id),message]);navigator.vibrate?.([25,30,25]);}catch(_){}});return()=>es.close();},[sessionId,reveal,onConfirmedSpin,externalPresentation]);
  const operationKey=()=>globalThis.crypto?.randomUUID?.()||`${Date.now()}-${Math.random()}`;
  const markDelivered=async(eventId)=>{if(delivering.has(eventId))return;setDelivering(keys=>new Set(keys).add(eventId));try{await api.post(`/api/live/${sessionId}/games/wheel/prizes/${eventId}/deliver`,{idempotency_key:operationKey()});setHistory(rows=>rows.map(row=>row.id===eventId?{...row,result:{...row.result,delivery_status:"delivered"}}:row));}catch(e){onError(e?.response?.data?.detail||"No pudimos marcar el premio como entregado.");}finally{setDelivering(keys=>{const next=new Set(keys);next.delete(eventId);return next;});}};
  const sendPrivate=async(eventId)=>{const text=privateDrafts[eventId]?.trim();if(!text||delivering.has(eventId))return;setDelivering(keys=>new Set(keys).add(eventId));try{await api.post(`/api/live/${sessionId}/games/wheel/prizes/${eventId}/private-message`,{text,idempotency_key:operationKey()});setPrivateDrafts(drafts=>({...drafts,[eventId]:""}));setHistory(rows=>rows.map(row=>row.id===eventId?{...row,result:{...row.result,delivery_status:"delivered"}}:row));}catch(e){onError(e?.response?.data?.detail||"No pudimos enviar el premio privado.");}finally{setDelivering(keys=>{const next=new Set(keys);next.delete(eventId);return next;});}};
  const save=async(next)=>{setSaving(true);try{const {data}=await api.put(`/api/live/${sessionId}/games/wheel`,next);setWheel(data);setDraft({...next,...data});setEditing(false);}catch(e){onError(e?.response?.data?.detail?.message||"No pudimos guardar la ruleta.");}finally{setSaving(false);}};
  const spin=async()=>{if(spinBusyRef.current||spinning)return;spinBusyRef.current=true;prepareMechanicalAudio();setSpinning(true);setResult(null);keyRef.current=keyRef.current||(globalThis.crypto?.randomUUID?.()||`${Date.now()}-${Math.random()}`);try{const {data}=await api.post(`/api/live/${sessionId}/games/wheel/spin`,{idempotency_key:keyRef.current});keyRef.current=null;spinBusyRef.current=false;if(!data.replayed&&wheel.spin_cost_energy)await onWallet();if(!externalPresentation){if(onConfirmedSpin)onConfirmedSpin(data);else reveal(data);}}catch(e){spinBusyRef.current=false;setSpinning(false);const d=e?.response?.data?.detail;if(d?.code==="INSUFFICIENT_ENERGY")onError("Energía KD9 insuficiente · Recargar",true);else onError(typeof d==="string"?d:"No pudimos activar el giro. Intenta nuevamente.");}};
  if(isHost&&editing)return <Editor value={draft} onChange={setDraft} onSave={()=>save(draft)} onClose={()=>setEditing(false)} saving={saving} live/>;
  return <div className="py-3 text-center" data-testid="temple-wheel-live">
    <div className="mb-3"><h3 className="font-cinzel text-lg text-violet-100">{wheel.title}</h3><p className="text-xs text-cyan-100">Costo real: {wheel.spin_cost_energy||0} KD</p></div>
    <RadialWheel wheel={wheel} angle={angle} result={result} rotorRef={rotorRef} pointerRef={pointerRef}/>
    {isHost?<div className="mt-4 grid grid-cols-4 gap-2">
      <button onClick={()=>save({...wheel,status:"active",enabled:true})} className="kd9-wheel-action-btn min-h-11 px-2 text-xs"><Play size={14}/>Activar</button>
      <button onClick={()=>save({...wheel,status:"paused",enabled:false})} className="kd9-wheel-action-btn min-h-11 px-2 text-xs"><Pause size={14}/>Desactivar</button>
      <button onClick={()=>setEditing(true)} className="kd9-wheel-action-btn min-h-11 px-2 text-xs"><Eye size={14}/>Editar</button>
      <button onClick={()=>save({...wheel,status:"finished",enabled:false})} className="kd9-wheel-action-btn min-h-11 px-2 text-xs"><Square size={14}/>Finalizar</button>
    </div>:<>
      <button disabled={spinning||wheel.status!=="active"} onClick={spin} className="kd9-wheel-spin-btn mt-5 min-h-12 w-full px-7 disabled:opacity-40"><RotateCw size={17}/>{spinning?"Acontecimiento en curso…":wheel.status==="active"?"Girar":"Ruleta pausada"}</button>
      {wallet.balance<(wheel.spin_cost_energy||0)&&<button onClick={()=>globalThis.location.assign("/app/energia-kd")} className="mt-3 text-sm text-cyan-200 underline">Recargar Energía KD9</button>}
    </>}
    {privateMessages.length>0&&<div className="mt-4 space-y-2" aria-live="polite">{privateMessages.slice(-3).map(message=><div key={message.id} className="animate-pulse rounded-xl border border-violet-300/40 bg-violet-950/70 p-3 text-left shadow-[0_0_20px_rgba(139,92,246,.18)]"><p className="flex items-center gap-2 text-xs font-bold text-violet-100"><MessageCircle size={14}/>Premio privado · {message.prize_label}</p><p className="mt-1 text-sm text-white/85">{message.text}</p><p className="mt-1 text-[10px] text-white/45">Solo visible para el ganador y el anfitrión durante este live.</p></div>)}</div>}
    {isHost&&<div className="mt-4 max-h-72 space-y-2 overflow-y-auto rounded-xl bg-white/5 p-2 text-left"><b className="text-xs text-white/60">Historial y entrega de premios</b>{history.map(e=><div key={e.id} className="border-t border-white/10 py-2 text-xs"><div className="flex items-center justify-between gap-2"><span>{e.player_name} · {e.result?.label}</span><span className={e.result?.delivery_status==="delivered"?"text-emerald-300":"text-amber-200"}>{e.result?.delivery_status==="delivered"?"Entregado":"Pendiente"}</span></div>
      {e.result?.delivery_mode==="live"&&e.result?.delivery_status!=="delivered"&&<button disabled={delivering.has(e.id)} onClick={()=>markDelivered(e.id)} className="kd9-btn-outline mt-2 min-h-11 w-full px-3 text-xs"><Check size={14}/>Marcar entregado</button>}
      {e.result?.delivery_mode==="private"&&e.result?.delivery_status!=="delivered"&&<div className="mt-2 space-y-2"><textarea className="kd9-input min-h-20 w-full p-2" maxLength="500" placeholder={`Mensaje privado para ${e.player_name}`} value={privateDrafts[e.id]||""} onChange={event=>setPrivateDrafts(drafts=>({...drafts,[e.id]:event.target.value}))}/><button disabled={delivering.has(e.id)||!privateDrafts[e.id]?.trim()} onClick={()=>sendPrivate(e.id)} className="kd9-btn-outline min-h-11 w-full px-3 text-xs"><MessageCircle size={14}/>Enviar una vez en privado</button></div>}
    </div>)}</div>}
  </div>;
}
